import { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Link,
} from "@mui/material";
import { Security } from "@mui/icons-material";
import SigilUiGridButton from "./SigilUiGridButton";
import { useSelfServeAlert } from "pages/SelfServe/utils/useSelfServeAlert";
import themes from "config/themes";
import { useGlobalConfig } from "providers/GlobalConfig";
import { useAuth } from "providers/Auth";
import { GlobalConfigType } from "types/configs";
import { ONBOARDING_STEPS } from "types/onboarding";
import { useSigilLinks } from "hooks/useSigilLinks";
import { membershipTypes } from "config/permissionDefs";
interface UpgradeButtonProps {
  showIcon?: boolean;
}

export const UpgradeButton = ({ showIcon = true }: UpgradeButtonProps) => {
  const [open, setOpen] = useState(false);
  const [isFreePlan, setIsFreePlan] = useState(false);
  const { sendAlert } = useSelfServeAlert();
  const { globalConfig } = useGlobalConfig()! as GlobalConfigType;
  const { auth } = useAuth()!;

  const { links } = useSigilLinks();

  useEffect(() => {
    if (!globalConfig.tenant?.props) return;
    const freePlan =
      globalConfig.tenant.props.currentPlan === membershipTypes.free.name;
    setIsFreePlan(freePlan);
  }, [globalConfig]);

  const handleOpen = async () => {
    await sendAlert({
      // The user config can be absent (an internal session, or a tenant user with none).
      customerName: globalConfig.user?.user ?? auth.email ?? "unknown",
      step: ONBOARDING_STEPS.upgrade,
    });
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  if (!isFreePlan) {
    return null;
  }

  return (
    <>
      <SigilUiGridButton
        eventLabel="upgrade_button_clicked"
        label="Upgrade"
        startIcon={showIcon && <Security sx={{ mr: 1 }} />}
        onClick={handleOpen}
        sx={{
          backgroundColor: themes.palette.success.main,
          fontWeight: 600,
          "&:hover": { backgroundColor: themes.palette.success.dark },
        }}
      >
        Upgrade
      </SigilUiGridButton>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "24px", pb: 1 }}>
          Let's get your enforcement plan started
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            We’re excited that you want to continue using Sigil! Because
            enforcement requires a few brand-specific details, we’d like to
            learn a bit more before activating your account.
          </Typography>

          <Box
            sx={{
              bgcolor: "#e3f2fd",
              p: 2,
              borderRadius: 2,
              borderLeft: "4px solid #2196f3",
              mt: 3,
            }}
          >
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              Please{" "}
              <Link
                href={links.calendar}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 600 }}
              >
                schedule time with our team
              </Link>{" "}
              to complete onboarding. Or simply wait - our Success team will
              reach out soon with next steps.
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mt: 3, mb: 1, lineHeight: 1.6 }}>
            In the meantime, you can see our pricing{" "}
            <Link
              href={links.pricing}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ fontWeight: 600 }}
            >
              here
            </Link>
            .
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <SigilUiGridButton
            onClick={handleClose}
            eventLabel="upgrade_dialog_close_clicked"
            label="Got it"
          ></SigilUiGridButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
